import { gql } from '@/__generated__/gql';
import {
  AutoPlanStatus,
  InvestorDetailsGetInvestorLegalEntitiesQuery,
  InvestorDetailsPacingTabAutoPlanMutation,
  StartDateOptions
} from '@/__generated__/graphql';
import { ApproveIconWithDollarIcon } from '@/assets/icons/pricing-tab-icons/approveIconWithDollar';
import { AutoPlanIcon } from '@/assets/icons/pricing-tab-icons/autoPlan';
import { CommitmentPlanIcon } from '@/assets/icons/pricing-tab-icons/commitmentPlan';
import { ForecastIcon } from '@/assets/icons/pricing-tab-icons/forecast';
import { TimeframeIcon } from '@/assets/icons/pricing-tab-icons/timeframe';
import Button from '@/components/basicComponents/button';
import { ISelectValue } from '@/components/basicComponents/select';
import { ITab } from '@/components/basicComponents/tabs';
import { Loader } from '@/components/loaders/loader';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useStatusData } from '@/hooks/useStatusData';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { InvestorDetailsLayout } from '../../investorDetailsLayout';
import { InvestorDetailsEntityTypeEnum } from '../../types';
import CommitmentPlanStatuses from './components/commitmentPlanStatuses';
import { PacingDetails } from './components/pacingDetails';
import { AnnualVsTargetAllocationSection } from './components/sections/allocationForecast';
import { CashFlowForecastSection } from './components/sections/cashFlowForecast';
import { CommitmentPlanSection } from './components/sections/commitmentPlan';
import { CumulativeCashFlowSection } from './components/sections/cumulativeCashFlow';
import { EconomicExposureSection } from './components/sections/economicExposure';
import { ForecastSummarySection } from './components/sections/forecastSummary';
import { NavForecastSection } from './components/sections/navForecast';
import { NetCashFlowsSection } from './components/sections/netCashFlows';
import { SectionsNav } from './components/sectionsNav';
import AutoPlanWizardModalPage from './modalPages/autoPlanWizardModalPage';
import ExportCSV from './modals/exportCSV';
import { COMMITMENT_PLAN_QUERY, COMMITMENT_PLANS_QUERY, PACING_TAB_AUTO_PLAN_MUTATION, PACING_TAB_AUTO_PLAN_STATUS_QUERY } from './queries';

export const LEGAL_ENTITY_IGNORE_ALLOCATION_TARGETS_QUERY = gql(`
  query InvestorDetailsPacingTabLegalEntityIgnoreAllocationTargets($id: String!) {
    legalEntity(id: $id) {
      entityName
      ignoreAllocationTargets
    }
  }
`);

const zeroStateItems = [
  { icon: <TimeframeIcon />, title: 'Set a timeframe to achieve allocation targets' },
  { icon: <CommitmentPlanIcon />, title: 'Automatically generate a commitment plan' },
  { icon: <ForecastIcon />, title: 'View quarterly cashflow and NAV projections' },
  { icon: <ApproveIconWithDollarIcon />, title: 'Pace new commitments according to plan' }
];

interface PricingTabProps {
  entityType: InvestorDetailsEntityTypeEnum;
  investorData: {
    investorName: string;
    advisorName: string;
    legalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
    legalEntitiesTotalCount: number;
  };
  selectedGroupName: string;
  entitiesSelectOptions: ISelectValue[];
  tabs: ITab[];
  resetEntityType: () => void;
}

export const PricingTab = ({ entityType, investorData, selectedGroupName, entitiesSelectOptions, tabs, resetEntityType }: PricingTabProps) => {
  const params = useParams();
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const { data: statusData } = useStatusData();

  const { searchParams, setSearchParams, makeScroll, saveScrollPostion } = useSearchParamsManager();

  const entityId = params?.entityId || '';
  const commitmentPlanId = searchParams.get('commitmentPlanId') || '';
  const openAutoPlanWizard = searchParams.get('openAutoPlanWizard') || '';
  const position = searchParams.get('position') || 0;

  const [entityName, setEntityName] = useState('');
  const [ignoreAllocationTargets, setIgnoreAllocationTargets] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'auto-plan' });
  const [commitmentPlanSelectOptions, setCommitmentPlanSelectOptions] = useState<ISelectValue[]>([]);
  const [zeroStateData, setZeroStateData] = useState<InvestorDetailsPacingTabAutoPlanMutation['autoPlan'] | null>(null);
  const [autoPlanStatus, setAutoPlanStatus] = useState<AutoPlanStatus | null>(null);
  const [isAutoPlanWizardPageOpen, setIsAutoPlanWizardPageOpen] = useState(false);

  const [getZeroStateData, { loading: getZeroStateDataLoading }] = useMutation(PACING_TAB_AUTO_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
    }
  });

  const { loading: legalEntityIgnoreAllocationTargetsLoading } = useQuery(LEGAL_ENTITY_IGNORE_ALLOCATION_TARGETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId,
    variables: {
      id: entityId
    },
    onCompleted: (data) => {
      const { entityName, ignoreAllocationTargets } = data.legalEntity;
      setEntityName(entityName);
      setIgnoreAllocationTargets(ignoreAllocationTargets);
    }
  });

  const {
    data: commitmentPlansData,
    loading: commitmentPlansLoading,
    refetch: commitmentPlansRefetch
  } = useQuery(COMMITMENT_PLANS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        legalEntityId: entityId
      }
    },
    onCompleted: (data) => {
      setCommitmentPlanSelectOptions(
        data.commitmentPlans.data.map((plan) => ({ value: plan.id, label: plan.active ? `${plan.name} (Active)` : plan.name }))
      );

      if (commitmentPlanId && data.commitmentPlans.data.length) {
        const isCommitmentPlanExist = data.commitmentPlans.data.some((plan) => plan.id === commitmentPlanId);
        if (!isCommitmentPlanExist) {
          const activeCommitmentPlan = data.commitmentPlans.data.find((plan) => plan.active);

          setSearchParams(
            (prev) => {
              prev.set('commitmentPlanId', activeCommitmentPlan ? activeCommitmentPlan?.id : data.commitmentPlans.data[0].id);
              return prev;
            },
            { replace: true }
          );
        }
        return;
      }
      if (!commitmentPlanId && data.commitmentPlans.data.length) {
        const activeCommitmentPlan = data.commitmentPlans.data.find((plan) => plan.active);
        setSearchParams(
          (prev) => {
            prev.set('commitmentPlanId', activeCommitmentPlan ? activeCommitmentPlan?.id : data.commitmentPlans.data[0].id);
            return prev;
          },
          { replace: true }
        );
      }
    }
  });

  useEffect(() => {
    if (!openAutoPlanWizard) return;

    if (openAutoPlanWizard === 'true') {
      handleAutoPlanWizardPage({ open: true });
    }
  }, [openAutoPlanWizard]);

  useEffect(() => {
    if (!commitmentPlansData || commitmentPlansLoading || commitmentPlansData.commitmentPlans.data.length || ignoreAllocationTargets) return;

    getZeroStateData({
      variables: {
        input: {
          name: 'Plan Example',
          startDate: StartDateOptions.Today,
          growthRate: 0,
          legalEntityId: entityId,
          biteSize: 0,
          yearsToAchieveAllocationTarget: 10,
          generateAllocations: false
        }
      },
      onCompleted: (data) => {
        setZeroStateData(data.autoPlan);
      }
    });
  }, [commitmentPlansData]);

  const {
    data: commitmentPlanData,
    loading: commitmentPlanLoading,
    refetch: commitmentPlanRefetch,
    networkStatus: commitmentPlanNetworkStatus
  } = useQuery(COMMITMENT_PLAN_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !commitmentPlanId || !commitmentPlanSelectOptions.length,
    variables: {
      id: commitmentPlanId
    },
    onCompleted: (data) => {
      setAutoPlanStatus(data.commitmentPlan.autoPlanStatus);
      makeScroll(position);
    }
  });

  const { refetch: autoPlanStatusRefetch } = useQuery(PACING_TAB_AUTO_PLAN_STATUS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !commitmentPlanId || !commitmentPlanSelectOptions.length,
    variables: {
      id: commitmentPlanId
    },
    onCompleted: (data) => {
      setAutoPlanStatus(data.commitmentPlan.autoPlanStatus);
    }
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      autoPlanStatusRefetch();
    }, 60000);

    if (autoPlanStatus !== AutoPlanStatus.InProgress) {
      clearInterval(intervalId);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [autoPlanStatus]);

  const openModalWindow = (type: string) => {
    setModalWindow({ type, isOpen: true });
  };

  const closeModalWindow = () => {
    setModalWindow({ ...modalWindow, isOpen: false });
  };

  const onChangeCommitmentPlan = (id: string) => {
    setSearchParams(
      (prev) => {
        prev.set('commitmentPlanId', id);
        return prev;
      },
      { replace: true }
    );
  };

  const openOpportunityDetailsPage = (id: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${id}?backToTitle=Investor Details`);
  };

  const handleAutoPlanWizardPage = ({ open, withRefetch }: { open: boolean; withRefetch?: boolean }) => {
    setIsAutoPlanWizardPageOpen(open);
    if (withRefetch) {
      commitmentPlansRefetch();
    }
  };

  if (isAutoPlanWizardPageOpen) {
    return <AutoPlanWizardModalPage entityType={entityType} backToTitle="Investor Details" handleAutoPlanWizardPage={handleAutoPlanWizardPage} />;
  }

  if (
    legalEntityIgnoreAllocationTargetsLoading ||
    commitmentPlanNetworkStatus === NetworkStatus.loading ||
    commitmentPlanNetworkStatus === NetworkStatus.setVariables ||
    commitmentPlansLoading ||
    getZeroStateDataLoading
  ) {
    return (
      <InvestorDetailsLayout
        entityType={entityType}
        investorData={investorData}
        selectedGroupName={selectedGroupName}
        entitiesSelectOptions={entitiesSelectOptions}
        tabs={tabs}
        resetEntityType={resetEntityType}
      >
        <Loader />
      </InvestorDetailsLayout>
    );
  }

  if (!commitmentPlansData?.commitmentPlans?.data?.length) {
    return (
      <InvestorDetailsLayout
        entityType={entityType}
        investorData={investorData}
        selectedGroupName={selectedGroupName}
        entitiesSelectOptions={entitiesSelectOptions}
        tabs={tabs}
        resetEntityType={resetEntityType}
      >
        {/* {modalWindow.isOpen && modalWindow.type === 'auto-plan' && (
          <AutoPlan
            isOpen={modalWindow.isOpen}
            onClose={closeModalWindow}
            entityName={entityName}
            openAutoPlanDialogFrom="pacingTab"
            commitmentPlansRefetch={commitmentPlansRefetch}
          />
        )} */}
        {!ignoreAllocationTargets && (
          <PacingDetails mode="zeroState" detailsData={zeroStateData} drawdownTargetDialogData={zeroStateData?.drawdownTargets ?? null} />
        )}
        <PaddingWrap>
          <NoInvestorsWrapper>
            <NoInvestorsTitle>
              <img src="/static/investors-page/missing-mag-glass-graphic.png" draggable="false" alt="No Investors" />
              <div>
                <h2>Create a Commitment Plan</h2>
                <p>Automatically generate a commitment plan and customize it as needed</p>
              </div>
            </NoInvestorsTitle>
            <NoInvestorsSteps>
              {zeroStateItems.map((item, index) => (
                <React.Fragment key={index}>
                  <NoInvestorsStep>
                    {item.icon}
                    <p>{item.title}</p>
                  </NoInvestorsStep>
                  {index !== zeroStateItems.length - 1 && (
                    <NoInvestorsStepSeparatorWrapper>
                      <div>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="21" viewBox="0 0 14 21" fill="none">
                          <path
                            d="M0.856689 2.85001L8.48669 10.5L0.856689 18.15L3.20669 20.5L13.2067 10.5L3.20669 0.5L0.856689 2.85001Z"
                            fill="#ABABAB"
                          />
                        </svg>
                      </div>
                    </NoInvestorsStepSeparatorWrapper>
                  )}
                </React.Fragment>
              ))}
            </NoInvestorsSteps>
            <AutoPlanButtonWrapper>
              <Button size="lg" onClick={() => handleAutoPlanWizardPage({ open: true })} icon={<AutoPlanIcon fill={themeVar('context.light')} />}>
                Auto Plan
              </Button>
            </AutoPlanButtonWrapper>
          </NoInvestorsWrapper>
        </PaddingWrap>
      </InvestorDetailsLayout>
    );
  }

  if (
    autoPlanStatus === AutoPlanStatus.InProgress ||
    autoPlanStatus === AutoPlanStatus.WaitingForReview ||
    autoPlanStatus === AutoPlanStatus.Failed
  ) {
    return (
      <InvestorDetailsLayout
        entityType={entityType}
        investorData={investorData}
        selectedGroupName={selectedGroupName}
        entitiesSelectOptions={entitiesSelectOptions}
        tabs={tabs}
        resetEntityType={resetEntityType}
      >
        <PacingDetails
          mode="waiting"
          detailsData={commitmentPlanData?.commitmentPlan ?? null}
          entityName={entityName}
          assetClassForecasts={commitmentPlanData?.commitmentPlan?.forecastSummary?.assetClassForecasts ?? []}
          selectedCommitmentPlanId={commitmentPlanId}
          commitmentPlanSelectOptions={commitmentPlanSelectOptions}
          drawdownTargetDialogData={commitmentPlanData?.commitmentPlan?.drawdownTargets ?? null}
          onChangeCommitmentPlan={onChangeCommitmentPlan}
          onEditButtonClick={() => openModalWindow('coming-soon')}
        />
        <CommitmentPlanStatuses
          status={autoPlanStatus}
          currentCommitmentPlan={commitmentPlanData?.commitmentPlan ?? null}
          commitmentPlanNetworkStatus={commitmentPlanNetworkStatus}
          commitmentPlansRefetch={commitmentPlansRefetch}
          commitmentPlanRefetch={commitmentPlanRefetch}
        />
      </InvestorDetailsLayout>
    );
  }

  return (
    <InvestorDetailsLayout
      entityType={entityType}
      investorData={investorData}
      selectedGroupName={selectedGroupName}
      entitiesSelectOptions={entitiesSelectOptions}
      tabs={tabs}
      resetEntityType={resetEntityType}
    >
      {modalWindow.isOpen && modalWindow.type === 'coming-soon' && (
        <ExportCSV isOpen={modalWindow.isOpen} onClose={closeModalWindow} planId={commitmentPlanData?.commitmentPlan?.id ?? null} />
      )}
      <PacingDetails
        mode="pacingTab"
        detailsData={commitmentPlanData?.commitmentPlan ?? null}
        entityName={entityName}
        assetClassForecasts={commitmentPlanData?.commitmentPlan?.forecastSummary?.assetClassForecasts ?? []}
        selectedCommitmentPlanId={commitmentPlanId}
        commitmentPlanSelectOptions={commitmentPlanSelectOptions}
        drawdownTargetDialogData={commitmentPlanData?.commitmentPlan?.drawdownTargets ?? null}
        onChangeCommitmentPlan={onChangeCommitmentPlan}
        onEditButtonClick={() => openModalWindow('coming-soon')}
      />
      <PricingTabWrapper isMobile={isMobile}>
        <SectionsNav commitmentPlanLoading={commitmentPlanLoading} />
        <SectionsWrapper>
          <ForecastSummarySection
            netCommitments={commitmentPlanData?.commitmentPlan?.forecast?.netCommitments ?? []}
            totalNAVs={commitmentPlanData?.commitmentPlan?.forecast?.totalNAVs ?? []}
            cumulativeContributions={commitmentPlanData?.commitmentPlan?.forecast?.cumulativeContributions ?? []}
            cumulativeDistributions={commitmentPlanData?.commitmentPlan?.forecast?.cumulativeDistributions ?? []}
          />
          <CommitmentPlanSection
            planAllocations={commitmentPlanData?.commitmentPlan?.planAllocations ?? []}
            targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
            commitmentPlanId={commitmentPlanData?.commitmentPlan?.id ?? null}
          />
          <AnnualVsTargetAllocationSection
            assetClassPercentages={commitmentPlanData?.commitmentPlan?.forecast?.assetClassPercentages ?? []}
            drawdownTarget={commitmentPlanData?.commitmentPlan?.drawdownTarget ?? 0}
          />
          <NavForecastSection
            assetClassNAVs={commitmentPlanData?.commitmentPlan?.forecast?.assetClassNAVs ?? []}
            existingCommitmentNAVs={commitmentPlanData?.commitmentPlan?.forecast?.existingCommitmentNAVs ?? []}
            targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
            commitmentPlanId={commitmentPlanData?.commitmentPlan?.id ?? null}
            openOpportunityDetailsPage={openOpportunityDetailsPage}
          />
          {/* <CommitmentsVsTargetAllocationSection /> */}
          <CashFlowForecastSection
            assetClassContributions={commitmentPlanData?.commitmentPlan?.forecast?.assetClassContributions ?? []}
            assetClassDistributions={commitmentPlanData?.commitmentPlan?.forecast?.assetClassDistributions ?? []}
            existingCommitmentContributions={commitmentPlanData?.commitmentPlan?.forecast?.existingCommitmentContributions ?? []}
            existingCommitmentDistributions={commitmentPlanData?.commitmentPlan?.forecast?.existingCommitmentDistributions ?? []}
            targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
            commitmentPlanId={commitmentPlanData?.commitmentPlan?.id ?? null}
            openOpportunityDetailsPage={openOpportunityDetailsPage}
          />
          <EconomicExposureSection
            totalNAVs={commitmentPlanData?.commitmentPlan?.forecast?.totalNAVs ?? []}
            uncalledCapital={commitmentPlanData?.commitmentPlan?.forecast?.uncalledCapital ?? []}
          />
          {/* <UnfundedCommitmentsAndNavSection
            totalNAVs={commitmentPlanData?.commitmentPlan?.forecast?.totalNAVs ?? []}
            uncalledCapital={commitmentPlanData?.commitmentPlan?.forecast?.uncalledCapital ?? []}
          /> */}
          <NetCashFlowsSection
            netContributions={commitmentPlanData?.commitmentPlan?.forecast?.netContributions ?? []}
            netDistributions={commitmentPlanData?.commitmentPlan?.forecast?.netDistributions ?? []}
          />
          <CumulativeCashFlowSection
            cumulativeContributions={commitmentPlanData?.commitmentPlan?.forecast?.cumulativeContributions ?? []}
            cumulativeDistributions={commitmentPlanData?.commitmentPlan?.forecast?.cumulativeDistributions ?? []}
          />
        </SectionsWrapper>
      </PricingTabWrapper>
    </InvestorDetailsLayout>
  );
};

const PricingTabWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '10px 16px' : '10px 50px')};
`;

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const NoInvestorsWrapper = styled.div`
  margin: 100px;
`;

const NoInvestorsTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${themeVar('font.weak')};
  padding-bottom: 40px;
  border-bottom: 1px solid ${themeVar('border.base')};
`;

const NoInvestorsSteps = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const NoInvestorsStep = styled.div`
  display: flex;
  width: 200px;
  height: 139px;
  padding: 10px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 15px;
  color: ${themeVar('font.base')};
  text-align: center;
`;

const NoInvestorsStepSeparatorWrapper = styled.div`
  display: flex;
  width: 40px;
  height: 40px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${themeVar('font.weak')};
  & > div {
    width: 20px;
    height: 12.35px;
    flex-shrink: 0;
  }
`;

const AutoPlanButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding-top: 40px;
`;
